import { useNode, useEditor } from "@craftjs/core";
import React, { useRef, useEffect, useState } from "react";
import { TabSettings } from "./settings";
import GrigoraCSSRenderer from "./../css-render";
import { InnerTab } from "../inner-tab/";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { RichTextInput } from "../../input";
import icon from "./icon";
import { CMSTabSettings } from "./cms-settings";
import classnames from "classnames";
import { getDynamicCSS, getSupportProps } from "../../block-supports";

export const Tab = (props) => {
  const {
    id,
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode((state) => ({
    id: state.id,
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));

  const {
    query: { node, parseFreshNode },
    actions: { add },
  } = useEditor();

  const effectCalled = useRef(0);

  const childrens = node(id).childNodes();

  const createNewTab = (index) => {
    const prevTabs = [...tab];
    const prevSubtitle = [...subtitle];
    prevTabs.push("New Tab");
    prevSubtitle.push("New subtitle");
    addTabChildrens(index);

    setProp((props) => {
      props.tab = prevTabs;
      props.subtitle = prevSubtitle;
    });
  };

  const addTabChildrens = (index) => {
    // A fresh Node is a partial Node object
    // where only the data.type property is required
    var freshNode = {
      id: `${id}-${index}`,
      data: {
        type: InnerTab,
        props: {},
        isCanvas: true,
      },
    };

    let nodeToAdd = parseFreshNode(freshNode).toNode();
    add(nodeToAdd, id);
  };

  useEffect(() => {
    if (effectCalled.current) {
      return;
    }
    effectCalled.current = true;
    if (childrens.length === 0) {
      addTabChildrens(1);
      addTabChildrens(2);
      addTabChildrens(3);
    }

    return () => {};
  }, []);

  const {
    children,
    tab = ["Tab 1", "Tab 2", "Tab 3"],
    subtitle = ["Subtitle 1", "Subtitle 2", "Subtitle 3"],
    initialOpen = 0,
    showTabSubtitle,
    tabPosition,
  } = props;

  var blockProps = getSupportProps({
    id,
    ...props,
  });

  const wrapperClass = classnames(
    {
      "g-tab": true,
      [`tab-${id}`]: id,
    },
    blockProps.className,
  );

  const [activeTab, setActiveTab] = useState(initialOpen);

  return (
    <div
      {...blockProps}
      ref={(ref) => connect(drag(ref))}
      className={wrapperClass}
      data-grigora-block="true"
      data-grigora-block-id={id}
    >
      <style>{`${getDynamicCSS(id, props)}`}</style>
      <style>{`${GrigoraCSSRenderer.blockCSS__Tab(
        id,
        props,
        activeTab,
      )}`}</style>
      <div className="tab-title-container">
        {tab.map((item, i) => {
          return (
            <div
              key={`${id}-${i}-title`}
              onClick={() => setActiveTab(i)}
              className={`tab-title ${Number(activeTab) === i ? "active" : ""}`}
            >
              <RichTextInput
                value={tab[i]}
                onChange={(e) => {
                  setProp((props) => {
                    const prevTab = [...tab];
                    prevTab[i] = e;
                    props.tab = prevTab;
                    return props;
                  }, 500);
                }}
                tagName="p"
              />
              {showTabSubtitle && (
                <RichTextInput
                  value={subtitle[i]}
                  onChange={(e) => {
                    setProp((props) => {
                      const prevSubtitle = [...subtitle];
                      prevSubtitle[i] = e;
                      props.subtitle = prevSubtitle;
                      return props;
                    }, 500);
                  }}
                  tagName="p"
                />
              )}
            </div>
          );
        })}
        <div className="tab-title-plus-icon-container">
          <FontAwesomeIcon
            onClick={() => {
              createNewTab(tab.length + 1);
            }}
            icon={faPlus}
            className="new-tab-icon"
          />
        </div>
      </div>
      <div className="tab-content-container">{children}</div>
    </div>
  );
};

// To Avoid Mangling in Production
Tab.displayName = "Tab";

Tab.craft = {
  name: "Tab",
  props: {},
  related: {
    CMSsettings: CMSTabSettings,
    settings: TabSettings,
    supports: [
      "grigora/entrance-animations",
      "grigora/css-transform-animations",
      "grigora/mouse-movement",
      "grigora/visibility",
      "grigora/position",
      "grigora/sticky",
      "grigora/filter",
      "grigora/custom-class",
      "grigora/custom-id",
    ],
    icon: icon,
    keywords: ["Tabs", "Accordion"],
    isHiddenInPostEditor: true,
  },
  custom: {
    name: "Tab",
    slug: "grigora/tab",
  },
};
